import { format, uiStrings } from '@satu/shared'
import { type ReactNode, useEffect, useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import { TAUTAN } from '@/routes/masuk/AuthCard'
import type { SyncState } from '../../db/protocol'
import {
  ALAMAT_BARU,
  bukaAlamatBaru,
  diAlamatLama,
  KUNCI_DARI_ALAMAT_LAMA,
  type PenyimpananAlamat,
  penyimpananPeramban,
  sudahPindah,
} from '../alamat'
import { getDb } from '../db'
import { jalankanPindah, keadaanPindah } from '../pindahAlamat'
import { lepasPenjagaTab, useJaga } from '../store/jaga'
import { useUi } from '../store/ui'

const teks = uiStrings.pindahAlamat

/** What the band needs of the page; the tests give their own. */
export interface LingkunganPita {
  hostname: string
  protocol: string
  storage: PenyimpananAlamat | null
  /** One sync run now, answering the state after it. */
  sinkronkan(): Promise<SyncState>
  /** Leaves for the new address. */
  buka(url: string): void
  sekarang(): string
}

function lingkunganPeramban(): LingkunganPita {
  return {
    hostname: window.location.hostname,
    protocol: window.location.protocol,
    storage: penyimpananPeramban(),
    sinkronkan: () => getDb().syncNow(),
    buka: (url) => window.location.assign(url),
    sekarang: () => new Date().toISOString(),
  }
}

/**
 * The band's frame: the surface tone under the header, as the offline and update bands. Release
 * 33's install band (./PitaPasang) takes it too.
 */
export function Pita({
  children,
  nama,
}: {
  children: ReactNode
  nama: 'pindah' | 'pasang-ulang' | 'pasang'
}) {
  return (
    <div
      {...{ [`data-pita-${nama}`]: '' }}
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-surface px-4 py-2 text-label text-text md:px-6 lg:px-8"
    >
      {children}
    </div>
  )
}

/** "Perangkat ini sudah pindah ke satu.katedralagats.or.id." with the address as the link. */
function KalimatSudahPindah({ protocol }: { protocol: string }) {
  const [depan, belakang] = teks.sudahPindah.split('{alamat}')
  return (
    <p className="min-w-[16rem] flex-1">
      {depan}
      <a href={bukaAlamatBaru(protocol)} className={TAUTAN}>
        {ALAMAT_BARU}
      </a>
      {belakang}
    </p>
  )
}

/**
 * Release 26: on the old address only, a persistent band above the content, in the shell's calm
 * band style (never a dialog; work goes on beneath it): *SATU pindah alamat ke …* with **Pindah
 * sekarang**, enabled only when nothing can be lost (./pindahAlamat `keadaanPindah`), otherwise
 * disabled with its one reason and, when a sync would help, **Sinkronkan sekarang**. Pressed
 * (after *Tinggalkan tanpa menyimpan?* if a form holds unsaved changes): the final sync, the
 * move recorded, Masuk on the new address. A moved device's band reads *Perangkat ini sudah
 * pindah ke …* with the link, and its saves are refused (./db). Outside the shell (Masuk, the
 * PIN) there is no session to send with: the sentence, or the moved link, only.
 */
export function PitaPindahAlamat({
  diLuarShell = false,
  lingkungan,
}: {
  diLuarShell?: boolean
  lingkungan?: LingkunganPita
}) {
  const [env] = useState(() => lingkungan ?? lingkunganPeramban())
  const sync = useUi((s) => s.sync)
  const online = useUi((s) => s.online)
  const [pindah, setPindah] = useState(() => sudahPindah(env.storage))
  const [sedang, setSedang] = useState(false)
  const idAlasan = useId()

  if (!diAlamatLama(env.hostname)) return null

  if (pindah) {
    return (
      <Pita nama="pindah">
        <KalimatSudahPindah protocol={env.protocol} />
      </Pita>
    )
  }

  const kalimat = (
    <p className="min-w-[16rem] flex-1">{format(teks.kalimat, { alamat: ALAMAT_BARU })}</p>
  )
  if (diLuarShell) return <Pita nama="pindah">{kalimat}</Pita>

  const keadaan = keadaanPindah(sync, online)
  const alasan = sedang || keadaan.boleh ? null : keadaan.alasan
  const sinkronkan = !sedang && !keadaan.boleh && keadaan.sinkronkan

  const mulai = async () => {
    setSedang(true)
    const pergi = await jalankanPindah({
      ...env,
      buka: (url) => {
        // The move is the app's own navigation: the browser's "leave site?" must not hold it.
        lepasPenjagaTab()
        env.buka(url)
      },
    })
    if (pergi) setPindah(true)
    setSedang(false)
  }

  return (
    <Pita nama="pindah">
      {kalimat}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p id={idAlasan} role="status">
          {alasan ?? ''}
        </p>
        {sinkronkan ? (
          <Button variant="link" onClick={() => void env.sinkronkan().catch(() => {})}>
            {teks.sinkronkanSekarang}
          </Button>
        ) : null}
        <Button
          variant="outline"
          disabled={sedang || !keadaan.boleh}
          aria-describedby={alasan === null ? undefined : idAlasan}
          onClick={() => {
            if (!sedang) useJaga.getState().tanya(() => void mulai())
          }}
        >
          {sedang ? teks.memindahkan : teks.tombol}
        </Button>
      </div>
    </Pita>
  )
}

/**
 * Release 26, on the new address: an app installed from the old address stays bound to it, so a
 * device that arrived from there (Masuk with `?dari=alamat-lama`) reads once, in the shell after
 * signing in, *Pasang ulang SATU dari alamat baru …*, until **Tutup**. While it shows, release
 * 33's install band (./PitaPasang) waits, so one band asks for the install, not two.
 */
export function PitaPasangUlang({
  lingkungan,
}: {
  lingkungan?: Pick<LingkunganPita, 'hostname' | 'storage'>
}) {
  const [env] = useState(
    () => lingkungan ?? { hostname: window.location.hostname, storage: penyimpananPeramban() },
  )
  const [tampil, setTampil] = useState(() => {
    if (diAlamatLama(env.hostname)) return false
    try {
      return env.storage?.getItem(KUNCI_DARI_ALAMAT_LAMA) != null
    } catch {
      return false
    }
  })
  if (!tampil) return null
  return (
    <Pita nama="pasang-ulang">
      <p className="min-w-[16rem] flex-1">{teks.pasangUlang}</p>
      <Button
        variant="ghost"
        onClick={() => {
          try {
            env.storage?.removeItem(KUNCI_DARI_ALAMAT_LAMA)
          } catch {
            // Storage gone: the note simply leaves for this visit.
          }
          setTampil(false)
        }}
      >
        {teks.tutup}
      </Button>
    </Pita>
  )
}

/**
 * Masuk on the new address with `?dari=alamat-lama` (release 26): the line that says why the
 * device starts empty, and the note the shell shows once after signing in.
 */
export function useSambutAlamatBaru(search: string): string | null {
  const dari =
    new URLSearchParams(search).get('dari') === 'alamat-lama' &&
    typeof window !== 'undefined' &&
    !diAlamatLama(window.location.hostname)
  useEffect(() => {
    if (!dari) return
    try {
      penyimpananPeramban()?.setItem(KUNCI_DARI_ALAMAT_LAMA, '1')
    } catch {
      // Storage refused: the welcome still shows; only the reinstall note is skipped.
    }
  }, [dari])
  return dari ? teks.selamatDatang : null
}
